import { useState } from 'react';
import { Check } from 'lucide-react';
import type { SlotDefinition } from '@volley/crucible/interview/types.js';

export interface SlotCardSingleSelectProps {
  slot: SlotDefinition;
  value: string | null;
  /** Accepts null when the user clears a custom "Other" answer. */
  onChange: (next: string | null) => void;
  prefilledUntouched: boolean;
  newUi?: boolean;
}

export function SlotCardSingleSelect({
  slot,
  value,
  onChange,
  prefilledUntouched,
  newUi = false,
}: SlotCardSingleSelectProps): React.JSX.Element {
  const options = slot.options ?? [];
  const [otherText, setOtherText] = useState(() =>
    value != null && !options.includes(value) ? value : '',
  );

  if (options.length === 0) {
    return (
      <div
        data-testid={`slot-card-single-select-${slot.id}`}
        data-empty="true"
        className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/80"
      >
        No options were provided for this question. Skip to continue.
      </div>
    );
  }

  if (!newUi) {
    return (
      <div
        data-testid={`slot-card-single-select-${slot.id}`}
        className="grid gap-1.5"
      >
        {options.map((opt) => {
          const selected = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              data-testid={`slot-card-single-select-${slot.id}-option`}
              data-option={opt}
              data-selected={selected ? 'true' : 'false'}
              className={`flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors ${
                selected
                  ? 'border-crucible-accent/60 bg-crucible-accent/10'
                  : 'border-white/10 bg-black/20 hover:border-crucible-accent/40 hover:bg-crucible-accent/10'
              } ${selected && prefilledUntouched ? 'opacity-60' : ''}`}
            >
              <span className={`text-sm ${selected ? 'font-medium text-white' : 'text-white/80'}`}>
                {opt}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  const handleOptionClick = (opt: string): void => {
    setOtherText('');
    onChange(opt);
  };

  const handleOtherChange = (text: string): void => {
    setOtherText(text);
    const trimmed = text.trim();
    onChange(trimmed || null);
  };

  return (
    <div
      data-testid={`slot-card-single-select-${slot.id}`}
      className="flex flex-col"
      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      {options.map((opt) => {
        const isListOptionSelected = value === opt && !otherText;
        const isPrefilled = isListOptionSelected && prefilledUntouched;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => handleOptionClick(opt)}
            data-testid={`slot-card-single-select-${slot.id}-option`}
            data-option={opt}
            data-selected={value === opt ? 'true' : 'false'}
            className="flex items-center justify-between py-3 text-left transition-colors hover:bg-white/[0.03] rounded-md px-1"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <span
              className="text-[14px] leading-snug"
              style={{
                color: isListOptionSelected ? 'var(--text-1)' : 'var(--text-2)',
                opacity: isPrefilled ? 0.6 : 1,
              }}
            >
              {opt}
            </span>
            {isListOptionSelected && (
              <Check
                size={15}
                strokeWidth={2}
                style={{ color: 'var(--color-crucible-accent, #7C6FF7)', flexShrink: 0 }}
              />
            )}
          </button>
        );
      })}

      {/* "Other" free-text — typed answer deselects list options */}
      <input
        type="text"
        value={otherText}
        onChange={(e) => handleOtherChange(e.target.value)}
        placeholder="Other…"
        className="mt-2 w-full bg-transparent py-2 px-1 text-[14px] outline-none placeholder:text-white/25"
        style={{ color: 'var(--text-1)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      />
    </div>
  );
}
